import './PartialReverse.scss';

import { Button, Chip, Chips } from '@modulor/react';
import { useSkeletonTranslation } from 'hooks/useSkeletonTranslation';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { getClickStreamEvent, getEventAnalytic, useGetClickStreamCashierContext } from 'services/clickstream';
import { FlowTypes } from 'store/constants';
import { useAppSelector } from 'store/hooks';
import type {
  PayHubCashierContractsPendingWithdrawalResponse,
  PayHubCashierContractsReversePendingWithdrawalV2Item,
} from 'store/models';
import { currencyDisplaySelector } from 'store/slices/deposit';
import { initialThemeSelector } from 'store/slices/global';
import { getFormattedAmountWithCurrency, getTheme, updateLogoUrlViaTheme } from 'utils';
import { getIconUrl } from 'utils/pwContext';

import { ReverseHeader } from '../ReverseHeader';
import { FULL_REVERSE_PERCENT } from '../utils';
import { PendingWithdrawalCard } from './PendingWithdrawalCard';
import {
  calculateReverseAmounts,
  calculateTotalReturn,
  getAvailableOrderIds,
  getAvailablePercents,
  getPreselectedPercent,
  getReverseAnalyticsData,
  isPercentAvailable,
} from './utils';

const clickstream = getEventAnalytic();

interface PartialReverseProps {
  withdrawals: PayHubCashierContractsPendingWithdrawalResponse[];
  preselectedOption: number;
  precision?: number;
  isFetching: boolean;
  onReturn: (items: PayHubCashierContractsReversePendingWithdrawalV2Item[]) => void;
  onDeposit: () => void;
}

export const PartialReverse: FC<PartialReverseProps> = ({
  withdrawals,
  preselectedOption,
  precision,
  isFetching,
  onReturn,
  onDeposit,
}) => {
  const { translate } = useSkeletonTranslation();
  const cashierContext = useGetClickStreamCashierContext(FlowTypes.withdrawal);
  const currencyDisplaySetting = useAppSelector(currencyDisplaySelector);
  const initialTheme = useAppSelector(initialThemeSelector) || getTheme();
  const currency = withdrawals[0]?.currency;
  const availablePercents = getAvailablePercents(withdrawals);

  const [selectedPercent, setSelectedPercent] = useState<number | null>(() =>
    getPreselectedPercent(preselectedOption, withdrawals),
  );
  const [checkedIds, setCheckedIds] = useState<number[]>(() =>
    selectedPercent === null ? [] : getAvailableOrderIds(withdrawals, selectedPercent),
  );

  const formatAmount = (amount: number) =>
    getFormattedAmountWithCurrency(`${amount}`, currency, currencyDisplaySetting);

  const getBelowLimitText = (remainAmount: number, minLimit?: number | null) =>
    minLimit
      ? translate('PH.DEPOSIT.PARTIAL_REVERSE.BELOW_LIMIT_WITH_VALUE', {
          remain: formatAmount(remainAmount),
          limit: formatAmount(minLimit),
        })
      : translate('PH.DEPOSIT.PARTIAL_REVERSE.BELOW_LIMIT');

  const getAnalyticsData = (ids: number[], percent: number | null) =>
    getReverseAnalyticsData(withdrawals, ids, percent, precision);

  useEffect(() => {
    clickstream.push(getClickStreamEvent.pw_homepage_popup_cancel_withdrawal_view, [cashierContext]);
  }, []);

  const selectPercent = (percent: number) => {
    const ids = getAvailableOrderIds(withdrawals, percent);

    setSelectedPercent(percent);
    setCheckedIds(ids);
    clickstream.push(
      getClickStreamEvent.pw_homepage_popup_cancel_withdrawal_option_selected(getAnalyticsData(ids, percent)),
      [cashierContext],
    );
  };

  const toggleWithdrawal = (orderId: number) => {
    const isChecked = checkedIds.includes(orderId);
    const ids = isChecked ? checkedIds.filter((id) => id !== orderId) : [...checkedIds, orderId];

    setCheckedIds(ids);
    clickstream.push(
      getClickStreamEvent.pw_homepage_popup_cancel_withdrawal_transaction_toggled(
        isChecked ? 'uncheck' : 'check',
        getAnalyticsData(ids, selectedPercent),
      ),
      [cashierContext],
    );
  };

  const totalReturn =
    selectedPercent === null ? 0 : calculateTotalReturn(withdrawals, checkedIds, selectedPercent, precision);
  const isReturnDisabled = selectedPercent === null || checkedIds.length === 0 || isFetching;

  const handleReturn = () => {
    if (selectedPercent === null) {
      return;
    }

    clickstream.push(
      getClickStreamEvent.cashier_homepage_popup_cancel_withdrawal_confirm_button_click(
        getAnalyticsData(checkedIds, selectedPercent),
      ),
      [cashierContext],
    );
    onReturn(checkedIds.map((orderId) => ({ order_id: orderId, reverse_percent: selectedPercent })));
  };

  const handleDeposit = () => {
    clickstream.push(
      getClickStreamEvent.cashier_homepage_popup_cancel_withdrawal_not_confirm_button_click(
        getAnalyticsData(checkedIds, selectedPercent),
      ),
      [cashierContext],
    );
    onDeposit();
  };

  return (
    <div className="partial-reverse">
      <ReverseHeader
        title={translate('PH.DEPOSIT.PARTIAL_REVERSE.TITLE')}
        subtitle={translate('PH.DEPOSIT.PARTIAL_REVERSE.BODY')}
      >
        <Chips
          dataTestId="partial-reverse-chips"
          className="partial-reverse__chips"
          selectedValues={selectedPercent === null ? [] : [selectedPercent]}
        >
          {availablePercents.map((percent) => (
            <Chip
              key={percent}
              value={percent}
              dataTestId={`partial-reverse-chip-${percent}`}
              checked={selectedPercent === percent}
              onClick={() => selectPercent(percent)}
            >
              {percent === FULL_REVERSE_PERCENT ? translate('PH.DEPOSIT.PARTIAL_REVERSE.ALL') : `${percent}%`}
            </Chip>
          ))}
        </Chips>
      </ReverseHeader>

      <div className="partial-reverse__list">
        {withdrawals.map((withdrawal) => {
          const isDisabled = selectedPercent !== null && !isPercentAvailable(withdrawal, selectedPercent);
          const amounts =
            selectedPercent === null ? null : calculateReverseAmounts(withdrawal.amount, selectedPercent, precision);

          return (
            <PendingWithdrawalCard
              key={withdrawal.order_id}
              orderId={withdrawal.order_id}
              logo={updateLogoUrlViaTheme(getIconUrl(withdrawal.logo) ?? '', initialTheme)}
              name={withdrawal.pay_group_name}
              amount={formatAmount(withdrawal.amount)}
              returnAmount={amounts ? formatAmount(amounts.returnAmount) : undefined}
              remainAmount={amounts ? formatAmount(amounts.remainAmount) : undefined}
              checked={!isDisabled && checkedIds.includes(withdrawal.order_id)}
              disabled={isDisabled}
              errorText={
                isDisabled && amounts ? getBelowLimitText(amounts.remainAmount, withdrawal.min_limit) : undefined
              }
              onToggle={toggleWithdrawal}
            />
          );
        })}
      </div>

      <div className="partial-reverse__buttons">
        <Button variant="deposit" disabled={isReturnDisabled} onClick={handleReturn}>
          {totalReturn > 0
            ? translate('PH.BUTTON.PARTIAL_REVERSE', {
                amount: formatAmount(totalReturn),
              })
            : translate('PH.BUTTON.PARTIAL_REVERSE_EMPTY')}
        </Button>
        <Button variant="secondary" onClick={handleDeposit}>
          {translate('CASHIER.DEPOSIT')}
        </Button>
      </div>
    </div>
  );
};
